import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from 'components/I18n';
import {
  useInterval,
  useHover,
  useIsSmallScreen,
} from 'utils/hooks';
import visibilityManager from 'utils/visibilityManager';
import { actionKey } from 'utils/helpers';
import { useIsKeyboardNavigation } from 'utils/useA11yNavigation';
import { useCarouselManager } from './carouselManager';
import Pager from './Pager';
import ItemRenderer from './ItemRenderer';
import NextIcon from 'components/Icons/NextIcon';
import {
  Wrapper,
  NavigationContainer,
  Container,
  PagerContainer,
} from './Styles';

const isAutoRotate = (availArr, pos) => {
  if (!availArr?.length) return true;

  return availArr[pos];
};

// How does carousel work:
// it renders only 3 items at the same time:
// [0] [1] [2]
//      ^ is visible due to css transform
// to switch to the next item carousel applies a css transform:
// [0] [1] [2]
//          ^ is visible due to css transform
// transform is animated, and when animation is done, carousel renders new items:
// [1] [2] [3]
//      ^ is visible due to css transform
// Due to "key" on ItemRenderer, React reuses items [1] and [2], and only [3] is being rendered
//
// So this is basically "virtualized" carousel, and it can handle a large number of items
// without a performance impact or increased memory/network usage

function Carousel({
  size,
  withPager,
  withNavigation,
  interval,
  children: renderItem,
  isRTL,
  autoRotateAvailability = [],
  getSlideLabel,
}) {
  const i18n = useI18n();
  const wrapperRef = React.useRef();
  const [stopInterval, setStopInterval] = React.useState(false);
  const containerRef = React.useRef();
  const shouldFocusRef = React.useRef(false);

  const {
    pos,
    prevPos,
    nextPos,
    setPos,
    animatedPos,
    animateNextPos,
    animatePrevPos,
  } = useCarouselManager(containerRef, size, isRTL);

  const isHover = useHover(wrapperRef);
  const isMobile = useIsSmallScreen();
  const isKeyboardNav = useIsKeyboardNavigation();

  let intervalMs = interval * 1000;
  if (interval && isHover) { // add 10sec to interval on hover (if timer is on)
    intervalMs += 10 * 1000;
  }
  if (stopInterval || !isAutoRotate(autoRotateAvailability, pos) || isKeyboardNav) {
    intervalMs = null;
  }
  useInterval(animateNextPos, intervalMs);

  const isAutoRotating = intervalMs !== null && intervalMs > 0;

  // on/off autorotation depends on component visibility
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return () => {};

    visibilityManager.add(el, v => setStopInterval(!v));
    return () => visibilityManager.remove(el);
  }, []);

  const onNextHandler = useCallback(() => {
    if (!stopInterval) {
      animateNextPos();
    }
  }, [stopInterval]);

  const handleNavClick = (e, animateFn) => {
    if (e.detail === 0) {
      shouldFocusRef.current = true;
    }
    animateFn();
  };

  const prevPosRef = React.useRef(pos);
  useEffect(() => {
    if ((isKeyboardNav || shouldFocusRef.current) && prevPosRef.current !== pos) {
      shouldFocusRef.current = false;
      const activeItem = containerRef.current?.children[1];
      const firstFocusable = activeItem?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
    prevPosRef.current = pos;
  }, [pos, isKeyboardNav]);

  const srOnlyStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
  };

  if (size === 1) {
    return renderItem(0);
  }

  return (
    <Wrapper
      innerRef={wrapperRef}
      onClick={() => setStopInterval(true)}
      onTouchStart={() => setStopInterval(true)}
    >
      <div style={srOnlyStyle} aria-live={isAutoRotating ? 'off' : 'polite'}>
        {getSlideLabel?.(pos)}
      </div>

      {withNavigation && !isMobile && (
        <NavigationContainer
          type="left"
          forceVisible={isKeyboardNav}
          onClick={e => handleNavClick(e, animatePrevPos)}
          role="button"
          tabIndex={0}
          aria-label={i18n.formatText('aria.carousel.previousSlide')}
          onKeyDown={actionKey(animatePrevPos)}
        >
          <NextIcon />
        </NavigationContainer>
      )}

      <Container innerRef={containerRef}>
        <ItemRenderer
          key={prevPos}
          pos={prevPos}
          renderItem={renderItem}
        />
        <ItemRenderer
          key={pos}
          pos={pos}
          renderItem={renderItem}
          next={onNextHandler}
          active
        />
        <ItemRenderer
          // When size === 2, nextPos === prevPos and React shows warning about duplicate key.
          // So we shouldn't add key in that case
          key={size === 2 ? undefined : nextPos}
          pos={nextPos}
          renderItem={renderItem}
        />
      </Container>

      {withNavigation && !isMobile && (
        <NavigationContainer
          type="right"
          forceVisible={isKeyboardNav}
          onClick={e => handleNavClick(e, animateNextPos)}
          role="button"
          tabIndex={0}
          aria-label={i18n.formatText('aria.carousel.nextSlide')}
          onKeyDown={actionKey(animateNextPos)}
        >
          <NextIcon right />
        </NavigationContainer>
      )}

      {withPager && (
        <PagerContainer>
          <Pager
            size={size}
            selectedPos={animatedPos}
            onClick={isMobile ? undefined : setPos}
          />
        </PagerContainer>
      )}
    </Wrapper>
  );
}
Carousel.propTypes = {
  size: PropTypes.number.isRequired,
  children: PropTypes.func.isRequired,
  withPager: PropTypes.bool,
  withNavigation: PropTypes.bool,
  interval: PropTypes.number.isRequired,
  isRTL: PropTypes.bool,
  autoRotateAvailability: PropTypes.arrayOf(PropTypes.bool),
  getSlideLabel: PropTypes.func,
};

export default React.memo(Carousel);
